import React, { useRef } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { agentUploadAPI } from '../utils/api';
import { toast } from 'sonner';

const PURPOSE_OPTIONS = [
  { key: 'electricity_bill', label: 'Electricity Bill', short: 'Bill' },
  { key: 'dg_nameplate', label: 'DG Nameplate', short: 'DG plate' },
  { key: 'dg_logbook', label: 'DG Logbook', short: 'DG log' },
  { key: 'generic', label: 'Other / Just store', short: 'Other' },
];

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;

const guessPurpose = (name) => {
  const l = name.toLowerCase();
  if (l.includes('bill') || l.includes('electricity') || l.includes('utility')) return 'electricity_bill';
  if (l.includes('nameplate') || l.includes('rating')) return 'dg_nameplate';
  if (l.includes('log') || l.includes('runtime')) return 'dg_logbook';
  return 'generic';
};

/**
 * Controlled file-attach widget.
 *
 * Props:
 *  - ownerType / ownerId  — passed when uploading
 *  - attachments          — array from PARENT state (source of truth)
 *  - setAttachments       — setter from PARENT state
 *  - disabled
 *
 * Each attachment: { file, purpose, status:'queued'|'uploading'|'done'|'failed', upload?, error? }
 */
export function ChatFileAttach({ ownerType = 'session', ownerId = null, attachments, setAttachments, disabled }) {
  const inputRef = useRef(null);
  const triggerPicker = () => inputRef.current?.click();

  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per message`);
      return;
    }
    const next = [...attachments];
    for (const f of files) {
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB`);
        continue;
      }
      next.push({ file: f, purpose: guessPurpose(f.name), status: 'queued' });
    }
    setAttachments(next);
    e.target.value = '';
  };

  const setPurpose = (idx, purpose) => {
    setAttachments(attachments.map((p, i) => i === idx ? { ...p, purpose } : p));
  };

  const removeFile = (idx) => {
    setAttachments(attachments.filter((_, i) => i !== idx));
  };

  if (!attachments || attachments.length === 0) {
    return (
      <>
        <input ref={inputRef} type="file" hidden multiple
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={onFilePick} data-testid="chat-attach-input" />
        <Button type="button" variant="ghost" size="sm" onClick={triggerPicker} disabled={disabled}
          className="h-9 w-9 p-0 text-slate-500 hover:text-slate-700"
          data-testid="chat-attach-btn">
          <Paperclip className="w-4 h-4" />
        </Button>
      </>
    );
  }

  return (
    <div className="w-full" data-testid="chat-attach-list">
      <input ref={inputRef} type="file" hidden multiple
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={onFilePick} />
      <div className="flex flex-wrap gap-2 mb-2">
        {attachments.map((p, i) => (
          <div key={p.name || i} className="bg-slate-50 border border-slate-200 rounded-lg p-2 flex items-center space-x-2 max-w-xs"
            data-testid={`attach-item-${i}`}>
            <div className="flex-shrink-0">
              {p.file.type.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-slate-500" /> : <FileText className="w-4 h-4 text-slate-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">{p.file.name}</p>
              <select value={p.purpose} onChange={e => setPurpose(i, e.target.value)} disabled={p.status === 'uploading' || p.status === 'done'}
                className="text-[10px] text-slate-500 bg-transparent border-0 focus:ring-0 focus:outline-none p-0 mt-0.5"
                data-testid={`attach-purpose-${i}`}>
                {PURPOSE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex-shrink-0">
              {p.status === 'queued' && (
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-slate-700">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {p.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" />}
              {p.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
              {p.status === 'failed' && <X className="w-3.5 h-3.5 text-red-500" />}
            </div>
          </div>
        ))}
        <button type="button" onClick={triggerPicker} disabled={attachments.length >= MAX_FILES}
          className="border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 hover:border-slate-400 hover:text-slate-700 disabled:opacity-50"
          data-testid="chat-attach-add-more">
          <Paperclip className="w-3.5 h-3.5 inline mr-1" /> Add more
        </button>
      </div>
    </div>
  );
}

/**
 * Imperative helper used by the chat send handler.
 * Receives the parent's attachments + setter — uploads each file in sequence,
 * mutates statuses, and returns the array of upload_ids that succeeded.
 */
export async function uploadAttachments({ attachments, setAttachments, ownerType = 'session', ownerId = null }) {
  if (!attachments || attachments.length === 0) return [];
  const next = [...attachments];
  const successIds = [];
  for (let i = 0; i < next.length; i++) {
    if (next[i].status === 'done') {
      if (next[i].upload?.upload_id) successIds.push(next[i].upload.upload_id);
      continue;
    }
    next[i] = { ...next[i], status: 'uploading' };
    setAttachments([...next]);
    try {
      const fd = new FormData();
      fd.append('file', next[i].file);
      fd.append('owner_type', ownerType);
      if (ownerId) fd.append('owner_id', ownerId);
      fd.append('purpose', next[i].purpose);
      fd.append('auto_extract', 'true');
      const res = await agentUploadAPI.upload(fd);
      next[i] = { ...next[i], status: 'done', upload: res.data };
      successIds.push(res.data.upload_id);
    } catch (e) {
      next[i] = { ...next[i], status: 'failed', error: e?.response?.data?.detail || 'Upload failed' };
      toast.error(`${next[i].file.name}: ${next[i].error}`);
    }
    setAttachments([...next]);
  }
  return successIds;
}

export default ChatFileAttach;
